import React, { useState, useEffect, useMemo } from "react";
import {
	Space,
	Dropdown,
	Menu,
	Modal,
	Button,
	Input,
	Spin,
	Upload,
	Checkbox,
} from "antd";
import {
	CheckOutlined,
	CloseCircleOutlined,
	DownloadOutlined,
	InfoCircleFilled,
	InfoCircleOutlined,
	PlusOutlined,
	RightCircleOutlined,
	UploadOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import debounce from "lodash/debounce";
import {
	ActionButton,
	AntdButton,
	AntdTable,
	ConfirmButton,
	Container,
	ContainerTitle,
	FilterActions,
	FilterContainer,
	Filters,
	Inner,
	ModalContainer,
	Section,
	TableContainer,
	UploadFile,
} from "./styles";
import PrePosTable from "./components/pre-pos-table/PrePosTable";
import {
	confirm_imported_whatsapp_products,
	delete_whatsapp_product,
	export_whatsapp_products,
	get_available_whatsapp_products,
	get_whatsapp_products,
	import_whatsapp_products,
	update_whatsapp_products,
} from "../../../../api/endpoints/agents";
import {
	openNotificationWithIcon,
	TYPE,
} from "../../../../utils/notificationToast";
import { COLORS, Typo } from "../../../layout/theme";
import { calculatePercentage } from "../../../../utils/calculatePrice";
import { Link, useParams } from "react-router-dom";
import AntdSearch from "../../../common/antd-search/AntdSearch";
import DropShadow from "../../../common/drop-shadow/DropShadow";
import SignInTemplate from "../../../layout/sign-in-template/SignInTemplate";
import Main from "../../../layout/main/Main";
import SecondaryButton from "../../../common/antd-buttons/SecondaryButton";
import PrimaryButton from "../../../common/antd-buttons/PrimaryButton";
import moment from "moment";
import ImportResumeCard from "./components/resume-card/ImportResumeCard";
import FakeProgress from "../../../common/fake-progress-bar/FakeProgressBar";
import TerciaryButton from "../../../common/antd-buttons/TerciaryButton";
import Image from "../../../common/image/Image";
import { CONSTANTS } from "../../../../utils/constants";
import AntdCheckbox from "../../../common/antd-checkbox/AntdCheckbox";

const { confirm } = Modal;

export default function WhatsAppProductList() {
	const { id: whatsappId } = useParams();
	const [loading, setLoading] = useState(false);
	const [whatsappInfo, setWhatsappInfo] = useState(null);
	const [products, setProducts] = useState([]);
	const [availableProducts, setAvailableProducts] = useState([]);
	const [search, setSearch] = useState("");
	const [searchAdd, setSearchAdd] = useState("");
	const [searchModalShow, setSearchModalShow] = useState(false);
	const [toogleAdd, setToogleAdd] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [page, setPage] = useState(1);
	const [key, setKey] = useState(null);
	const [importPreview, setImportPreview] = useState(null);
	const [importModalOpen, setImportModalOpen] = useState(false);
	const [showUploadFileModal, setShowUploadFileModal] = useState(false);
	const [showImportPreviewModal, setShowImportPreviewModal] = useState(false);
	const [importResponse, setImportResponse] = useState(null);
	const [replaceAll, setReplaceAll] = useState(false);
	const [isImportingFile, setIsImportingFile] = useState(false);
	const [isConfirmingImport, setIsConfirmingImport] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const fetchProducts = async () => {
		if (!whatsappId) return;

		setLoading(true);
		try {
			const res = await get_whatsapp_products(whatsappId);

			setWhatsappInfo(res);
			setProducts(
				res?.products
					?.map((p) => ({
						...p,
						key: p.productId,
						hasChanges: false,
					}))
					?.sort((a, b) =>
						a.productTitle.toLowerCase() < b.productTitle.toLowerCase()
							? -1
							: 1,
					) ?? [],
			);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudieron cargar los productos del agente",
				"Intentá nuevamente o contactá con soporte.",
			);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchProducts();
	}, [whatsappId]);

	const fetchAvailableProducts = async (value = "") => {
		setLoading(true);
		try {
			const res = await get_available_whatsapp_products({
				whatsappId,
				search: value,
				skip: 0,
				take: 50,
			});

			setAvailableProducts(
				res?.map((p) => ({
					...p,
					key: p.productId,
					hasChanges: true,
				})) ?? [],
			);

			setSearchModalShow(true);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudieron cargar productos disponibles",
				"Intentá nuevamente.",
			);
		} finally {
			setLoading(false);
		}
	};

	const eventHandler = (e) => {
		setSearch(e.target.value);
	};

	const debouncedEventHandler = useMemo(() => debounce(eventHandler, 500), []);

	const eventHandlerAdd = (e) => {
		const value = e.target.value;
		setSearchAdd(value);
		fetchAvailableProducts(value);
	};

	const debouncedEventHandlerAdd = useMemo(
		() => debounce(eventHandlerAdd, 600),
		[whatsappId],
	);

	useEffect(() => {
		return () => {
			debouncedEventHandler.cancel();
			debouncedEventHandlerAdd.cancel();
		};
	}, []);

	const filteredProducts = useMemo(() => {
		if (!search) return products;

		const lower = search.toLowerCase();

		return products.filter((p) => {
			return (
				p.productTitle?.toLowerCase().includes(lower) ||
				p.productEan13?.toLowerCase().includes(lower) ||
				p.productPresentation?.toLowerCase().includes(lower) ||
				p.brandName?.toLowerCase().includes(lower)
			);
		});
	}, [products, search]);

	const onInputChange = (field, index) => (e) => {
		const newData = [...products];
		const absoluteIndex = page === 1 ? index : (page - 1) * 300 + index;

		const value = Number(e.target.value);

		newData[absoluteIndex][field] = value;

		if (field === "productTriggerQty" && value === 0) {
			newData[absoluteIndex].productTriggerDiscount = 0;
		}

		newData[absoluteIndex].hasChanges = true;

		setProducts(newData);
		setHasChanges(true);
	};

	const handleAddProduct = (index) => {
		const selected = availableProducts[index];

		if (!selected) return;

		const exists = products.some((p) => p.productId === selected.productId);

		if (exists) {
			openNotificationWithIcon(
				TYPE.WARNING,
				"Producto ya agregado",
				"Este producto ya forma parte del pool del agente.",
			);
			return;
		}

		setProducts((prev) => [
			...prev,
			{
				...selected,
				hasChanges: true,
				key: selected.productId,
			},
		]);

		setHasChanges(true);
		setAvailableProducts((prev) =>
			prev.filter((p) => p.productId !== selected.productId),
		);
	};

	const handleDeleteLocal = (index) => {
		const absoluteIndex = page === 1 ? index : (page - 1) * 300 + index;
		setProducts((prev) => prev.filter((_, idx) => idx !== absoluteIndex));
		setHasChanges(true);
	};

	const deleteRoute = (record) => {
		confirm({
			title: "¿Seguro que querés quitar este producto del agente?",
			icon: <CloseCircleOutlined style={{ color: "red" }} />,
			content: "La relación se quitará de inmediato.",
			okText: "Sí",
			okType: "default",
			cancelText: "No",
			onOk: async () => {
				await handleDeletePersisted(record);
			},
		});
	};

	const handleDeletePersisted = async (record) => {
		try {
			await delete_whatsapp_product(whatsappId, record.productId);
			openNotificationWithIcon(
				TYPE.SUCCESS,
				"Producto eliminado",
				"El producto se quitó del agente correctamente.",
			);
			fetchProducts();
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudo eliminar el producto",
				"Intentá nuevamente.",
			);
		}
	};

	const handleSubmit = () => {
		confirm({
			title: "¿Seguro que querés publicar estos productos?",
			icon: <InfoCircleOutlined style={{ color: "blue" }} />,
			content: "Los cambios se aplicarán de inmediato al agente vendedor.",
			okText: "Sí",
			okType: "default",
			cancelText: "No",
			onOk: confirmChanges,
		});
	};

	const confirmChanges = async () => {
		try {
			const body = {
				products: products.map((p) => ({
					productId: p.productId,
					productDiscount: Number(p.productDiscount ?? 0),
					productTriggerQty: Number(p.productTriggerQty ?? 0),
					productTriggerDiscount: Number(p.productTriggerDiscount ?? 0),
					productMinQty: Number(p.productMinQty ?? 1),
					productMaxQty: Number(p.productMaxQty ?? 500),
					productSaleCommision: Number(p.productSaleCommision ?? 0),
				})),
			};

			const res = await update_whatsapp_products(whatsappId, body);

			openNotificationWithIcon(
				TYPE.SUCCESS,
				res,
				"Los productos del agente se actualizaron correctamente.",
			);

			setHasChanges(false);
			setSearchModalShow(false);
			setToogleAdd(false);
			fetchProducts();
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudieron guardar los cambios",
				error?.response?.data ?? "Intentá nuevamente o contactá con soporte.",
			);
		}
	};

	const handleCancel = () => {
		setHasChanges(false);
		setSearchModalShow(false);
		setToogleAdd(false);
		fetchProducts();
	};

	const menuActive = (
		<Menu
			items={[
				{
					key: "delete",
					label: <Typo onClick={() => deleteRoute(key)}>Eliminar</Typo>,
				},
			]}
		/>
	);

	const availableColumns = [
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>Producto</div>
			),
			dataIndex: "productTitle",
			key: "productTitle",
			width: 450,
			render: (t, item) => (
				<ContainerTitle>
					<Typo type="primary" lineHeight={1} texto="medium" fontSize={16}>
						{t}
					</Typo>
					<Typo type="mediumGrey" lineHeight={1} fontSize={12}>
						{item?.productPresentation} · {item?.productEan13}
					</Typo>
				</ContainerTitle>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Descuento</div>,
			dataIndex: "productMobileDiscount",
			key: "productMobileDiscount",
			render: (_, item) => (
				<Typo type="success" lineHeight={1} level={5}>
					{item?.productMobileDiscount ?? item?.productDiscount ?? 0} %
				</Typo>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Cant. minima</div>,
			key: "minMax",
			render: (_, item) => (
				<Typo type="mediumGrey" lineHeight={1} level={6}>
					{item?.productMinQty}
				</Typo>
			),
		},
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>Agregar</div>
			),
			width: 100,
			key: "action",
			render: (_, record, index) => (
				<Container style={{ justifyContent: "center" }}>
					<Button
						type="primary"
						ghost
						onClick={() => handleAddProduct(index)}
						style={{ borderRadius: 12 }}
					>
						<RightCircleOutlined />
					</Button>
				</Container>
			),
		},
	];

	const columns = [
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>Producto</div>
			),
			dataIndex: "productTitle",
			key: "productTitle",
			width: 300,
			render: (t, item) => (
				<ContainerTitle>
					<Typo type="primary" lineHeight={1} texto="medium" fontSize={16}>
						{t}
					</Typo>
					<Typo type="mediumGrey" lineHeight={1} level={12}>
						{item?.productPresentation} · {item?.productEan13}
					</Typo>
				</ContainerTitle>
			),
		},
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>
					Desc. original
				</div>
			),
			width: 180,
			render: (_, item) => (
				<Container style={{ flexDirection: "column" }}>
					<Typo type="mediumGrey" lineHeight={1} level={5}>
						{item.productMobileDiscount ?? 0} %
					</Typo>
					{item?.productMobileTriggerQty > 0 && (
						<Typo type="mediumGrey" lineHeight={1} level={8}>
							A partir de {item.productMobileTriggerQty} un.{" "}
							{item.productMobileTriggerDiscount} %
						</Typo>
					)}
				</Container>
			),
		},
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>
					Descuento agente
				</div>
			),
			dataIndex: "productDiscount",
			width: 220,
			render: (value, record, index) => (
				<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
					<Input
						type="number"
						min={0}
						step={0.01}
						value={record.productDiscount}
						onChange={onInputChange("productDiscount", index)}
						addonAfter="%"
						style={{ width: "120px" }}
					/>
					{record?.productDiscount > 0 && (
						<Typo type="terciary" lineHeight={1} level={8} translate="no">
							PVP -
							{calculatePercentage(
								parseFloat(record?.productPrice),
								parseFloat(record?.clientGeneralDiscount),
								parseFloat(record?.productDiscount),
								0,
								0,
							)}
							%
						</Typo>
					)}
				</div>
			),
		},
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>
					Cant. minima
				</div>
			),
			width: 150,
			render: (_, record, index) => (
				<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
					<Input
						type="number"
						min={1}
						value={record.productMinQty}
						onChange={onInputChange("productMinQty", index)}
						addonAfter="un."
						style={{ width: "110px" }}
					/>
					{/* <Input
						type="number"
						min={1}
						value={record.productMaxQty}
						onChange={onInputChange("productMaxQty", index)}
						addonAfter="máx"
						style={{ width: "95px" }}
					/> */}
				</div>
			),
		},
		{
			title: () => (
				<div style={{ color: COLORS.White, textAlign: "center" }}>
					Descuento por cantidad
				</div>
			),
			width: 350,
			render: (_, record, index) => (
				<div style={{ display: "flex", alignItems: "center", gap: 10 }}>
					<Input
						type="number"
						min={0}
						value={record.productTriggerQty}
						onChange={onInputChange("productTriggerQty", index)}
						addonAfter="un."
						style={{ width: "110px" }}
					/>
					{record?.productTriggerQty > 0 && (
						<Input
							type="number"
							min={0}
							step={0.01}
							value={record.productTriggerDiscount}
							onChange={onInputChange("productTriggerDiscount", index)}
							addonAfter="%"
							style={{ width: "120px" }}
						/>
					)}
					{record?.productTriggerDiscount > 0 && (
						<Typo type="terciary" lineHeight={1} level={8} translate="no">
							PVP -
							{calculatePercentage(
								parseFloat(record?.productPrice),
								parseFloat(record?.clientGeneralDiscount),
								parseFloat(record?.productDiscount),
								parseFloat(record?.productTriggerDiscount),
								0,
							)}
							%
						</Typo>
					)}
				</div>
			),
		},
		hasChanges
			? {
					title: "",
					key: "action",
					width: 30,
					render: (_, record, index) => (
						<Space size="middle">
							<CloseCircleOutlined onClick={() => handleDeleteLocal(index)} />
						</Space>
					),
				}
			: {
					title: "",
					key: "id",
					dataIndex: "id",
					width: 30,
					render: (_, record) => (
						<Dropdown overlay={menuActive} placement="bottomRight">
							<ActionButton onMouseOver={() => setKey(record)}>
								<BsThreeDotsVertical />
							</ActionButton>
						</Dropdown>
					),
				},
	];

	const handleExportProducts = async () => {
		try {
			setIsExporting(true);

			const blob = await export_whatsapp_products({
				whatsAppId: whatsappId,
			});

			const href = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = href;

			const fileName =
				`Productos_Agente_WhatsApp_` + moment().format("DD-MM-YYYY") + ".xlsx";

			link.setAttribute("download", fileName);

			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(href);
		} catch (error) {
			console.log(error);
			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudo exportar el archivo",
				"Intentá nuevamente o contactá con soporte.",
			);
		} finally {
			setIsExporting(false);
		}
	};

	const handleImportFile = async (e) => {
		const file = e.fileList?.[e.fileList.length - 1]?.originFileObj;

		if (!file) return;

		try {
			setIsImportingFile(true);

			const formData = new FormData();
			formData.append("file", file);
			formData.append(
				"data",
				JSON.stringify({
					whatsAppId: whatsappId,
				}),
			);

			const res = await import_whatsapp_products(formData);

			setImportResponse(res);
			setShowUploadFileModal(false);
			setShowImportPreviewModal(true);

			if (res?.invalidRows > 0) {
				openNotificationWithIcon(
					TYPE.WARNING,
					"Archivo procesado con observaciones",
					`Se encontraron ${res.invalidRows} filas con errores.`,
				);
			} else {
				openNotificationWithIcon(
					TYPE.SUCCESS,
					"Archivo procesado correctamente",
					"Revisá la vista previa antes de confirmar.",
				);
			}
		} catch (error) {
			console.log(error);

			const fallback =
				error?.response?.data?.title ||
				error?.response?.data ||
				"El archivo debe estar en formato Excel o CSV y respetar las columnas esperadas.";

			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudo importar el archivo",
				fallback,
			);
		} finally {
			setIsImportingFile(false);
		}
	};

	const handleConfirmImport = () => {
		if (!importResponse) return;

		if (importResponse.invalidRows > 0) {
			openNotificationWithIcon(
				TYPE.ERROR,
				"No se puede confirmar",
				"El archivo tiene filas inválidas. Corregí el Excel y volvé a importarlo.",
			);
			return;
		}

		if (replaceAll) {
			Modal.confirm({
				title: "¿Reemplazar todo el pool del agente?",
				content:
					"Los productos actualmente asignados que no estén en este Excel serán quitados del agente. Esta acción impacta en lo que el agente puede vender.",
				okText: "Sí, reemplazar",
				cancelText: "Cancelar",
				onOk: confirmImport,
			});

			return;
		}

		confirmImport();
	};

	const confirmImport = async () => {
		try {
			setIsConfirmingImport(true);

			const res = await confirm_imported_whatsapp_products({
				whatsAppId: whatsappId,
				replaceAll,
				items: importResponse?.items ?? [],
			});

			if (res?.success) {
				openNotificationWithIcon(
					TYPE.SUCCESS,
					"Importación finalizada",
					res?.message ?? "Los productos del agente fueron actualizados.",
				);

				setImportResponse(null);
				setShowImportPreviewModal(false);
				setReplaceAll(false);
				fetchProducts();
			} else {
				openNotificationWithIcon(
					TYPE.ERROR,
					"No se pudo confirmar la importación",
					res?.message ?? "Revisá el archivo e intentá nuevamente.",
				);
			}
		} catch (error) {
			console.log(error);

			openNotificationWithIcon(
				TYPE.ERROR,
				"No se pudo confirmar la importación",
				error?.response?.data ?? "Intentá nuevamente o contactá con soporte.",
			);
		} finally {
			setIsConfirmingImport(false);
		}
	};

	const handleCancelImport = () => {
		setImportResponse(null);
		setShowImportPreviewModal(false);
		setReplaceAll(false);
	};

	const importPreviewColumns = [
		{
			title: () => <div style={{ color: COLORS.White }}>Fila</div>,
			dataIndex: "rowIndex",
			key: "rowIndex",
			width: 70,
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Producto</div>,
			dataIndex: "productTitle",
			key: "productTitle",
			width: 280,
			render: (t, item) => (
				<ContainerTitle>
					<Typo type={item.isInvalid ? "danger" : "primary"} level={6}>
						{t || "Producto no encontrado"}
					</Typo>
					<Typo type="muted" level={8}>
						{item.productPresentation || "-"} · EAN: {item.productEan || "-"}
					</Typo>
				</ContainerTitle>
			),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Estado</div>,
			key: "status",
			width: 130,
			render: (_, item) =>
				item.isInvalid ? (
					<Typo type="danger" level={7}>
						Error
					</Typo>
				) : item.alreadyAssigned ? (
					<Typo type="secondary" level={7}>
						Actualiza
					</Typo>
				) : (
					<Typo type="success" level={7}>
						Nuevo
					</Typo>
				),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Descuento</div>,
			dataIndex: "productDiscount",
			key: "productDiscount",
			width: 110,
			render: (v) => `${v ?? 0}%`,
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Mín/Máx</div>,
			key: "minMax",
			width: 120,
			render: (_, item) =>
				`${item.productMinQty ?? 1} / ${item.productMaxQty ?? 500}`,
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Trigger</div>,
			key: "trigger",
			width: 150,
			render: (_, item) =>
				item.productTriggerQty > 0 ? (
					<Typo type="muted" level={7}>
						{item.productTriggerQty} un. / {item.productTriggerDiscount}%
					</Typo>
				) : (
					<Typo type="muted" level={7}>
						Sin trigger
					</Typo>
				),
		},
		{
			title: () => <div style={{ color: COLORS.White }}>Errores</div>,
			key: "errors",
			width: 260,
			render: (_, item) =>
				item.errors?.length > 0 ? (
					<ul style={{ paddingLeft: 16, margin: 0 }}>
						{item.errors.map((err, idx) => (
							<li key={idx}>
								<Typo type="danger" level={8}>
									{err}
								</Typo>
							</li>
						))}
					</ul>
				) : (
					<Typo type="muted" level={8}>
						-
					</Typo>
				),
		},
	];

	return (
		<Main>
			<Main.Breadcrumb>
				<Main.BreadcrumbItem>
					<Link to="/">Home</Link>
				</Main.BreadcrumbItem>
				<Main.BreadcrumbSeparator />
				<Main.BreadcrumbItem>
					<Link to="/admin/laboratoryAgentProducts">Listado de agentes</Link>
				</Main.BreadcrumbItem>
				<Main.BreadcrumbSeparator />
				<Main.ActiveBreadcrumbItem>
					Productos agente {whatsappInfo?.phoneNumber ?? ""}
				</Main.ActiveBreadcrumbItem>
			</Main.Breadcrumb>
			<Main.Body>
				<Inner>
					<Filters>
						<FilterContainer>
							<div style={{ display: "flex", gap: 20, alignItems: "center" }}>
								<AntdSearch
									width={400}
									height={35}
									allowClear
									placeholder="Buscar asignados"
									onChange={debouncedEventHandler}
									style={{
										borderRadius: 12,
									}}
								/>
								{!toogleAdd && (
									<DropShadow type="drop">
										<PrimaryButton
											onClick={() => setToogleAdd(true)}
											icon={<PlusOutlined />}
											style={{ width: "200px", fontWeight: "400", height: 30 }}
										>
											Agregar producto
										</PrimaryButton>
									</DropShadow>
								)}
								{toogleAdd && (
									<div
										style={{ display: "flex", gap: 10, alignItems: "center" }}
									>
										<AntdSearch
											allowClear
											placeholder="Buscar producto disponible"
											width={250}
											height={35}
											value={searchAdd}
											onChange={(e) => setSearchAdd(e.target.value)}
											onPressEnter={(e) => debouncedEventHandlerAdd(e)}
											disabled={loading}
											style={{
												borderRadius: 12,
											}}
										/>
										<SecondaryButton
											onClick={() => fetchAvailableProducts(searchAdd)}
											style={{
												width: 120,
												height: 30,
											}}
										>
											Buscar
										</SecondaryButton>
										<CloseCircleOutlined
											style={{ color: COLORS.Danger }}
											onClick={() => {
												setToogleAdd(false);
												setSearchAdd("");
											}}
										/>
									</div>
								)}
							</div>
							<FilterActions>
								<SecondaryButton
									onClick={handleExportProducts}
									loading={isExporting}
									icon={<DownloadOutlined />}
									style={{ width: "190px", fontWeight: "400", height: 30 }}
								>
									Exportar Excel
								</SecondaryButton>

								<TerciaryButton
									onClick={() => setShowUploadFileModal(true)}
									loading={isImportingFile || isConfirmingImport}
									icon={<UploadOutlined />}
									style={{ width: "190px", fontWeight: "400", height: 30 }}
								>
									Importar Excel
								</TerciaryButton>
							</FilterActions>
						</FilterContainer>
					</Filters>

					{whatsappId && hasChanges && (
						<ConfirmButton clientId={whatsappInfo?.clientId}>
							<AntdButton onClick={handleSubmit} success>
								Confirmar cambios
							</AntdButton>{" "}
							<AntdButton onClick={handleCancel}>Cancelar cambios</AntdButton>
						</ConfirmButton>
					)}

					<TableContainer>
						<AntdTable
							dataSource={filteredProducts}
							columns={columns}
							loading={loading}
							pagination={{
								pageSize: 300,
								showSizeChanger: false,
								onChange: (page) => setPage(page),
							}}
							rowClassName={(record) =>
								record.hasChanges ? "hasChanges" : null
							}
						/>
					</TableContainer>

					<Modal
						open={searchModalShow}
						onCancel={() => setSearchModalShow(false)}
						width={950}
						centered
						footer={null}
						destroyOnClose
					>
						<ModalContainer>
							<PrePosTable
								data={availableProducts}
								columns={availableColumns}
								loading={loading}
								pagination={{
									pageSize: 50,
									showSizeChanger: false,
								}}
								scrollData={{ y: 350 }}
							/>
						</ModalContainer>
					</Modal>

					<Modal
						title={
							<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
								<Typo type="primary" level={6}>
									Importar productos del agente <InfoCircleFilled />
								</Typo>
								<Typo level={7}>Extensiones admitidas: .xlsx, .xls, .csv</Typo>
								<Typo level={7}>
									El cruce de productos se realiza por EAN y permite actualizar
									dinamicas.
								</Typo>
							</div>
						}
						open={showUploadFileModal}
						onCancel={() => setShowUploadFileModal(false)}
						centered={false}
						footer={null}
						destroyOnClose
					>
						<UploadFile
							multiple={false}
							showUploadList={false}
							beforeUpload={() => false}
							action={null}
							onChange={handleImportFile}
							disabled={isImportingFile || isConfirmingImport}
							accept=".xlsx,.xls,.csv"
						>
							{isImportingFile ? (
								<Spin size="default" />
							) : (
								<Section>
									<Image src={CONSTANTS.URL_SHEET_ICON} width="80px" />
									<Typo level={7}>
										Hacé click para elegir el archivo o arrastralo a este
										espacio
									</Typo>
									<Typo type="muted" level={8}>
										Columnas esperadas: EAN, Producto, Presentación, Precio,
										Descuento agente, Cantidad mínima, Cantidad máxima, Cantidad
										trigger, Descuento trigger y Comisión venta.
									</Typo>
								</Section>
							)}
						</UploadFile>
					</Modal>

					<Modal
						open={showImportPreviewModal}
						onCancel={handleCancelImport}
						width={1200}
						centered
						destroyOnClose
						footer={null}
						title={
							<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
								<Typo type="primary" level={6}>
									Vista previa de importación
								</Typo>
								<Typo type="muted" level={7}>
									Revisá los productos antes de aplicar cambios al agente.
								</Typo>
							</div>
						}
					>
						{importResponse && (
							<div
								style={{ display: "flex", flexDirection: "column", gap: 18 }}
							>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "repeat(5, 1fr)",
										gap: 12,
									}}
								>
									<ImportResumeCard
										title="Filas"
										value={importResponse.totalRows}
									/>
									<ImportResumeCard
										title="Válidas"
										value={importResponse.validRows}
									/>
									<ImportResumeCard
										title="Con error"
										value={importResponse.invalidRows}
										danger={importResponse.invalidRows > 0}
									/>
									<ImportResumeCard
										title="Nuevas"
										value={importResponse.newRows}
									/>
									<ImportResumeCard
										title="Ya asignadas"
										value={importResponse.alreadyAssignedRows}
									/>
								</div>

								<div
									style={{
										padding: 12,
										border: "1px solid #f0f0f0",
										borderRadius: 10,
										background: "#fafafa",
									}}
								>
									<AntdCheckbox
										checked={replaceAll}
										onChange={(e) => setReplaceAll(e.target.checked)}
									>
										<Typo type="primary" level={7}>
											Reemplazar completamente el pool del agente con este Excel
										</Typo>
									</AntdCheckbox>

									<Typo type="mediumGrey" level={8}>
										Si activás esta opción, los productos actualmente asignados
										al agente que no estén en el Excel serán quitados del pool.
									</Typo>
								</div>

								<AntdTable
									dataSource={
										importResponse.items?.map((item, index) => ({
											...item,
											key: `${item.productEan}-${index}`,
										})) ?? []
									}
									columns={importPreviewColumns}
									pagination={{
										pageSize: 10,
										showSizeChanger: false,
									}}
									scroll={{ y: 360 }}
									rowClassName={(record) =>
										record.isInvalid ? "hasError" : null
									}
								/>

								<div
									style={{
										display: "flex",
										justifyContent: "flex-end",
										gap: 12,
									}}
								>
									<AntdButton onClick={handleCancelImport} cancel>
										Cancelar
									</AntdButton>

									<AntdButton
										onClick={handleConfirmImport}
										success
										loading={isConfirmingImport}
										disabled={importResponse.invalidRows > 0}
									>
										<CheckOutlined /> Confirmar importación
									</AntdButton>
								</div>
							</div>
						)}
					</Modal>

					<Modal
						open={isConfirmingImport}
						footer={null}
						closable={false}
						centered
						destroyOnClose
					>
						<FakeProgress
							isLoading={isConfirmingImport}
							title={"Importando productos del agente"}
						/>
					</Modal>
				</Inner>
			</Main.Body>
		</Main>
	);
}
